import React, { useState, useEffect } from 'react';
import { IntegrationRequest } from '../../lib/tenant-api';
import Modal from '../Modal';
import type { IntegrationConfigProps } from './types';

export const WebflowConfig: React.FC<IntegrationConfigProps> = ({
  isOpen,
  onClose,
  integration,
  createIntegrationMutation,
  updateIntegrationMutation
}) => {
  const [formData, setFormData] = useState({
    name: '',
    site_id: '',
    api_token: '',
    is_enabled: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!integration;

  useEffect(() => {
    if (integration) {
      setFormData({
        name: integration.name,
        site_id: integration.config.site_id || '',
        api_token: integration.config.api_token || '',
        is_enabled: integration.is_enabled,
      });
    } else {
      setFormData({
        name: '',
        site_id: '',
        api_token: '',
        is_enabled: true,
      });
    }
    setErrors({});
  }, [integration, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Integration name is required';
    }

    if (!formData.site_id.trim()) {
      newErrors.site_id = 'Site ID is required';
    }

    if (!formData.api_token.trim()) {
      newErrors.api_token = 'API token is required';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const integrationData: IntegrationRequest = {
      integration_type: 'webflow',
      name: formData.name,
      config: {
        site_id: formData.site_id,
        api_token: formData.api_token,
      },
      is_enabled: formData.is_enabled,
    };

    if (isEditing && integration) {
      updateIntegrationMutation.mutate({
        id: integration.id,
        data: integrationData,
      });
    } else {
      createIntegrationMutation.mutate(integrationData);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      return newData;
    });

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const isLoading = createIntegrationMutation.isLoading || updateIntegrationMutation.isLoading;
  return <Modal
    isOpen={isOpen}
    onClose={handleClose}
    title={isEditing ? `Edit Webflow Integration` : `Connect Webflow`}
  >
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Integration Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
          Integration Name
        </label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
          placeholder="e.g., My Webflow Integration"
          disabled={isLoading}
        />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="site_id" className="block text-sm font-medium text-gray-700 mb-2">
          Site ID
        </label>
        <input
          type="text"
          id="site_id"
          value={formData.site_id}
          onChange={(e) => handleInputChange('site_id', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.site_id ? 'border-red-300' : 'border-gray-300'
            }`}
          placeholder="e.g., 1093432343"
          disabled={isLoading}
        />
        {errors.site_id && <p className="mt-1 text-sm text-red-600">{errors.site_id}</p>}
      </div>
      <div>
        <label htmlFor="api_token" className="block text-sm font-medium text-gray-700 mb-2">
          API Token
        </label>
        <input
          type="text"
          id="api_token"
          value={formData.api_token}
          onChange={(e) => handleInputChange('api_token', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.api_token ? 'border-red-300' : 'border-gray-300'
            }`}
          disabled={isLoading}
        />
        {errors.api_token && <p className="mt-1 text-sm text-red-600">{errors.api_token}</p>}
      </div>


      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isEditing ? 'Updating...' : 'Connecting...'}
            </>
          ) : (
            isEditing ? 'Update Integration' : 'Connect Webflow'
          )}
        </button>
      </div>
    </form>
  </Modal>

}
